/**
 * Feature E05-F06-T03 — pure model for the universal Stage-4/5/6 send dialog.
 *
 * Framework-free, side-effect-free helpers shared by `SendEmailDialog` and its
 * tests, ported from the legacy `dashboard.js` (_addRecipient / showStaffQuickPick
 * category filter / _onSrcTeamSelect). The recipient de-dup + team→To helpers live
 * in `@eer/shared/email-send` (parseRecipients / addRecipient / recipientsFromTeam);
 * this file owns only the staff quick-pick category filtering, which is web-only.
 */
import type { StaffContactDTO } from '@eer/shared';

/**
 * The default staff category the quick-pick filters to per item_key (legacy
 * `showStaffQuickPick(category)`): the Sourcing Sheet send pre-filters to
 * `sourcer`, the rest open with no filter ('all'). Consumers may still switch the
 * category dropdown manually.
 */
export const STAFF_QUICK_PICK_DEFAULT_CATEGORY: Record<string, string> = {
  sourcing_sheet_email: 'sourcer',
  client_followup_email: 'all',
  kickoff_sheet: 'all',
  internal_notification: 'notification',
  final_package: 'all',
};

/** Resolve the initial quick-pick category for an item_key (default 'all'). */
export function defaultQuickPickCategory(itemKey: string): string {
  return STAFF_QUICK_PICK_DEFAULT_CATEGORY[itemKey] ?? 'all';
}

/**
 * Filter the staff list for the quick-pick (legacy category filter). `'all'`
 * passes everyone; any other value keeps only active contacts of that category.
 * Inactive contacts are always dropped (they cannot receive mail).
 */
export function filterStaffByCategory(
  staff: readonly StaffContactDTO[],
  category: string,
): StaffContactDTO[] {
  const active = staff.filter((c) => c.is_active);
  if (!category || category === 'all') return active;
  return active.filter((c) => c.category === category);
}
